import moment from "moment";
import { Booking } from "./types";

/**
 * Determines if a booking is a transferred patient.
 * A transferred patient has a transferred_doctor_mapping_id that exists
 * AND differs from their original doctor_mapping_id.
 */
export const isTransferredPatient = (booking: Booking): boolean => {
  return (
    booking.transferred_doctor_mapping_id != null &&
    booking.transferred_doctor_mapping_id !== "" &&
    booking.doctor_mapping_id !== booking.transferred_doctor_mapping_id
  );
};

/**
 * Sorts bookings based on queue type, with proper handling for transferred patients.
 *
 * - Token / Slot_Token: non-transferred sort by token_number; if either is transferred, sort by check_in_time
 * - Slot: non-transferred use booked_slot_start (HH:mm:ss); transferred use check_in_time converted to local HH:mm:ss
 * - Session: all sort by check_in_time
 *
 * Ongoing patients (status 2, 8, 9) always appear first.
 */
export const sortQueueBookings = (
  a: Booking,
  b: Booking,
  queueType: string | undefined
): number => {
  const aOngoing =
    a.status === 2 || a.status === 8 || a.status === 9 || a.transfer_status === 2;
  const bOngoing =
    b.status === 2 || b.status === 8 || b.status === 9 || b.transfer_status === 2;

  // Always show ongoing patients at the top
  if (aOngoing && !bOngoing) return -1;
  if (!aOngoing && bOngoing) return 1;

  const aTransferred = isTransferredPatient(a);
  const bTransferred = isTransferredPatient(b);

  if (queueType === "Token" || queueType === "Slot_Token") {
    // Both non-transferred: sort by token_number
    if (!aTransferred && !bTransferred) {
      return a.token_number - b.token_number;
    }
    // Either is transferred: sort by check_in_time
    return moment(a.check_in_time).diff(moment(b.check_in_time));
  }

  if (queueType === "Slot") {
    // Get comparable HH:mm:ss for each booking
    const aTime = aTransferred
      ? moment(a.check_in_time).local().format("HH:mm:ss")
      : a.booked_slot_start;
    const bTime = bTransferred
      ? moment(b.check_in_time).local().format("HH:mm:ss")
      : b.booked_slot_start;

    return aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
  }

  if (queueType === "Session") {
    return moment(a.check_in_time).diff(moment(b.check_in_time));
  }

  // Default: sort by check_in_time
  return moment(a.check_in_time).diff(moment(b.check_in_time));
};
